import appConfig from '../../config/appConfig';
import {getDeviceTypesData, IDeviceDataTypeOptions} from '../../consts/device/deviceTypesData';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import arrayUtils from '../../utils/arrayUtils';
import uaUtils from '../../utils/uaUtils';

type TConfigurationCode = {
    configurationCodeByte0: number;
    configurationCodeByte1: number;
    configurationCodeByte2: number;
    configurationCodeByte3: number;
};
type TMaterial = {
    materialBrandFamily?: string;
    materialColor?: string;
    materialGroup?: string;
    materialModelNumber?: string;
};

const getDeviceTypesDataByType = (type: string): IDeviceDataTypeOptions | null => {
    if (!type) return null;

    const result = getDeviceTypesData()[type as DEVICE_TYPES];

    return result ? result : null;
};

const getIsHolderByType = (type: string) => getDeviceTypesDataByType(type)?.isHolder;
const getDeviceModelByType = (type: string) => getDeviceTypesDataByType(type)?.deviceModel;
const getDeviceModelNameByType = (type: string) => getDeviceTypesDataByType(type)?.modelName;
const getProductNameByType = (type?: string) => (type ? getDeviceTypesDataByType(type)?.name : undefined);
const isCortez = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER;
const isCosmos = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER;
const isCosmosHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40C_HOLDER_CONTROL;
const isOrdinary = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_V24P_CHARGER;
const isP1 = (type: DEVICE_TYPES) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P1;
const isP4 = (type: DEVICE_TYPES) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P4;
const isP1V4 = (type: DEVICE_TYPES) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P1_V4;
const isP1V41 = (type: string) => isHermes(type) || isHermesHolder(type) || isJuno(type) || isAlpha(type);
const isSoyuz = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER;
const isAriane = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1_ARIANE;
const isVespucci = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V30M;
const isOriginalOne = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31M;
const isOriginalDuo = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
const isVoyager = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER;
const isVoyagerHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40V_HOLDER_CONTROL;
const isDelta = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40D_CONTROL;
const isHermesPrime = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER;
const isHermesMid = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER;
const isHermesBeta = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41B_CHARGER;
const isJuno = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41J_CONTROL;
const isAlpha = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41A_CONTROL;
const isHermesPrimeHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41P_HOLDER_CONTROL;
const isHermesMidHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL;
const isHermesBetaHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41B_HOLDER_CONTROL;
const isHermes = (type: string) => isHermesMid(type) || isHermesPrime(type) || isHermesBeta(type);
const isHermesHolder = (type: string) =>
    isHermesMidHolder(type) || isHermesPrimeHolder(type) || isHermesBetaHolder(type);
const isMono = (type: string) => getDeviceTypesDataByType(type)?.isMono;
const isIluma = (type: string) => isCosmos(type) || isVoyager(type) || isDelta(type);
const isIlumaHolder = (type: string) => isCosmosHolder(type) || isVoyagerHolder(type);
const isP1Charger = (type: DEVICE_TYPES) => isP1(type) && !getDeviceTypesDataByType(type)?.isHolder;
const isP1V4Holder = (type: DEVICE_TYPES) => isP1V4(type) && getDeviceTypesDataByType(type)?.isHolder;
const isP1V4HolderOrMono = (type: DEVICE_TYPES) => isP1V4Holder(type) || (isP1V4(type) && isMono(type));
const isP1ChargerWithoutAriane = (type: DEVICE_TYPES) => isP1Charger(type) && !isAriane(type);
const isOriginalsDuoDeviceByCodeOrMaterial = ({
    configurationCode,
    material,
    isHolder,
}: {
    configurationCode?: TConfigurationCode;
    material?: TMaterial;
    isHolder?: boolean;
}): boolean | undefined => {
    const comparedByte = isHolder ? 2 : 1; //regional configuration(1 - original one charger, 2 - original one holder)

    if (configurationCode) {
        const isOriginalsDuoByCode = configurationCode.configurationCodeByte1 === comparedByte;

        return isOriginalsDuoByCode;
    }

    const {materialBrandFamily} = material || {};

    if (materialBrandFamily) {
        const isOriginalsDuoByMaterial = materialBrandFamily === 'IO';

        return isOriginalsDuoByMaterial;
    }
};

const getDevicePlatform = (deviceType: string) => {
    const type = getDeviceTypesDataByType(deviceType)?.type;

    if (!type) return null;

    switch (type) {
        case DEVICE_TYPES.DEV_TYPE_P1:
            return DEVICE_TYPES.DEV_TYPE_P1;
        case DEVICE_TYPES.DEV_TYPE_P4:
            return DEVICE_TYPES.DEV_TYPE_P4;
        case DEVICE_TYPES.DEV_TYPE_P1_V4:
            return DEVICE_TYPES.DEV_TYPE_P1_V4;
        default:
            return null;
    }
};

type TPlatformBrowserConfig = {
    platform: string;
    browser?: string;
    browsers?: string | string[];
};

type TBleDisabledDeviceConfig = {
    type: string;
    platform?: string | string[];
    platformBrowserConfig?: TPlatformBrowserConfig[];
};

type TBleDisabledDeviceTypesList = (string | TBleDisabledDeviceConfig)[];

const isDeviceBleDisabledByType = (type: string) => {
    const listOfBleDisabledDeviceTypes: TBleDisabledDeviceTypesList = appConfig.getBleDisabledForDevice();
    const {name: platformName} = uaUtils.getOS();
    const browserName = uaUtils.getBrowserName();
    const result = checkIfBleConnectionIsDisabled(listOfBleDisabledDeviceTypes, type, platformName, browserName);

    return result;
};

// Helper: Check if a platform string matches the config platform
const isPlatformMatch = (currentPlatform: string | undefined, configPlatform: string): boolean => {
    if (!currentPlatform) return true;
    return currentPlatform.toLowerCase().includes(configPlatform.toLowerCase());
};

// Helper: Check if a browser matches the config browser(s)
const isBrowserMatch = (
    currentBrowser: string | undefined,
    configBrowser?: string,
    configBrowsers?: string | string[]
): boolean => {
    // If no browser specified in config, match all browsers
    if (!configBrowser && !configBrowsers) return true;

    // If no current browser info, don't match
    if (!currentBrowser) return false;

    const browserLower = currentBrowser.toLowerCase();

    // Check single browser
    if (configBrowser && browserLower.includes(configBrowser.toLowerCase())) {
        return true;
    }

    // Check multiple browsers
    if (configBrowsers) {
        const browserList = arrayUtils.toArray(configBrowsers);

        return browserList.some((item) => browserLower.includes(item.toLowerCase()));
    }

    return false;
};

// Helper: Check if platformBrowserConfig matches
const isPlatformBrowserConfigMatch = (
    platformBrowserConfig: TPlatformBrowserConfig[],
    currentPlatform?: string,
    currentBrowser?: string
): boolean => {
    return platformBrowserConfig.some((pbConfig) => {
        const {platform: pbPlatform, browser: pbBrowser, browsers: pbBrowsers} = pbConfig;
        const platformMatches = isPlatformMatch(currentPlatform, pbPlatform);

        if (!platformMatches) return false;

        // If platform matches and no browser specified in config, disable for all browsers
        if (!pbBrowser && !pbBrowsers) return true;

        // Check if browser matches
        return isBrowserMatch(currentBrowser, pbBrowser, pbBrowsers);
    });
};

// Helper: Check if legacy platform config matches
const isLegacyPlatformMatch = (configPlatform: string | string[], currentPlatform?: string): boolean => {
    if (!currentPlatform) return true;

    const platformList = arrayUtils.toArray(configPlatform);

    return platformList.some((item) => isPlatformMatch(currentPlatform, item));
};

const checkIfBleConnectionIsDisabled = (
    listOfBleDisabledDeviceTypes: TBleDisabledDeviceTypesList,
    type: string,
    platform?: string,
    browser?: string
): boolean => {
    for (const configValue of listOfBleDisabledDeviceTypes) {
        // Simple string match: disable for all platforms/browsers
        if (typeof configValue === 'string') {
            if (configValue === type) return true;
            continue;
        }

        // Object config
        const {type: configType, platform: configPlatform, platformBrowserConfig} = configValue;

        if (configType !== type) continue;

        // New format: platformBrowserConfig takes priority
        if (platformBrowserConfig && platformBrowserConfig.length > 0) {
            if (isPlatformBrowserConfigMatch(platformBrowserConfig, platform, browser)) {
                return true;
            }
        }
        // Legacy format: platform-only config (backward compatibility)
        else if (configPlatform) {
            if (isLegacyPlatformMatch(configPlatform, platform)) {
                return true;
            }
        }
        // No platform config: disable for all
        else {
            return true;
        }
    }

    return false;
};

const mapHolderTypeToChargerType = (holderType: string, deviceType: string): string => {
    // RS: Cosmos & Voyager, Hermes Prime & Hermes Mid holders are same.
    // This logic is used to avoid mix between,
    // e.g., Cosmos charger asset & Voyager holder asset
    const isHolderP1V41 = isP1V41(holderType);

    if (isCosmos(deviceType) && !isHolderP1V41) {
        return DEVICE_TYPES.DEV_TYPE_P1V40C_HOLDER_CONTROL;
    }
    if (isVoyager(deviceType) && !isHolderP1V41) {
        return DEVICE_TYPES.DEV_TYPE_P1V40V_HOLDER_CONTROL;
    }
    if (isHermesPrime(deviceType) && isHolderP1V41) {
        return DEVICE_TYPES.DEV_TYPE_P1V41P_HOLDER_CONTROL;
    }
    if (isHermesMid(deviceType) && isHolderP1V41) {
        return DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL;
    }
    if (isHermesBeta(deviceType) && isHolderP1V41) {
        return DEVICE_TYPES.DEV_TYPE_P1V41B_HOLDER_CONTROL;
    }

    return holderType;
};

const areDevicesInSameGroup = (device1: DEVICE_TYPES, device2: DEVICE_TYPES): boolean => {
    const isP1Device1 = isP1(device1);
    const isP4Device1 = isP4(device1);
    const isP1V4Device1 = isP1V4(device1);
    const isArianeDevice1 = isAriane(device1);

    const isP1Device2 = isP1(device2);
    const isP4Device2 = isP4(device2);
    const isP1V4Device2 = isP1V4(device2);
    const isArianeDevice2 = isAriane(device2);

    if ((isArianeDevice1 && !isArianeDevice2) || (!isArianeDevice1 && isArianeDevice2)) return false;

    if (isP1Device1 && isP1Device2) return true;

    if (isP4Device1 && isP4Device2) return true;

    return isP1V4Device1 && isP1V4Device2;
};

export default {
    getIsHolderByType,
    getDeviceModelByType,
    getDevicePlatform,
    getProductNameByType,
    isCortez,
    isCosmos,
    isDelta,
    isMono,
    isOrdinary,
    isOriginalOne,
    isOriginalDuo,
    isHermesPrime,
    isHermesMid,
    isHermesBeta,
    isHermes,
    isJuno,
    isAlpha,
    isP1,
    isP1Charger,
    isP1V4,
    isP1V41,
    isP1V4Holder,
    isP1V4HolderOrMono,
    isP4,
    isAriane,
    isSoyuz,
    isVespucci,
    isVoyager,
    getDeviceTypesDataByType,
    isIluma,
    isIlumaHolder,
    isDeviceBleDisabledByType,
    isOriginalsDuoDeviceByCodeOrMaterial,
    isP1ChargerWithoutAriane,
    mapHolderTypeToChargerType,
    getDeviceModelNameByType,
    checkIfBleConnectionIsDisabled,
    areDevicesInSameGroup,
};
